import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ArrowRightIcon from "@mui/icons-material/ArrowCircleRight";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Paths } from "../../types/app";
import { theme } from "../../assets/theme";
import { useStandards } from "../../contexts/StandardsProvider";
import { getActionItems } from "../../services/standards-service";
import { IActionItemsByStandard } from "../../types/action-item";
import { getStats, postVerifiedScore } from "../../services/insights-service";

export default function ActionPlanVerificationHomePage() {
  const { submittedStandards } = useStandards();
  const navigate = useNavigate();
  const [actionItems, setActionItems] = useState<IActionItemsByStandard[]>();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [modalOpen, setModalOpen] = useState(false);
  const [currentScore, setCurrentScore] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const getActionPlans = async () => {
    const actionItemsByStandard = await Promise.all(
      submittedStandards.map(async (standard) => ({
        id: standard.id,
        actionItems: await getActionItems(standard.id),
      })),
    );

    setActionItems(actionItemsByStandard);
  };

  useEffect(() => {
    getActionPlans();
    // eslint-disable-next-line
  }, [submittedStandards]);

  const totalUnverifiedItems = actionItems
    ?.flatMap((s) => s.actionItems)
    .filter((item) => item.done && !item.verifiedAt).length ?? 0;

  const handleOpenModal = async () => {
    try {
      const stats = await getStats();
      setCurrentScore(stats.total.score ?? null);
    } catch {
      setCurrentScore(null);
    }
    setModalOpen(true);
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await postVerifiedScore();
      setModalOpen(false);
      navigate(0); // reload page to reflect new state
    } catch (e) {
      console.error("Failed to submit verified score", e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      component="main"
      sx={{ flex: 1, m: isMobile ? "15px 15px 30px 15px" : 5 }}
    >
      <div>
        {submittedStandards.length > 0 ? (
          <div>
            <Box sx={{ width: "100%", marginBottom: "15px" }}>
              <Chip
                label="Verification Process"
                sx={{
                  background: "#faaa0a",
                  fontSize: "24px",
                  paddingTop: "30px",
                  paddingBottom: "30px",
                }}
              />
              <Box
                sx={{ width: "100%" }}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              ></Box>
            </Box>
            <Grid container>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Standard</TableCell>
                      <TableCell align="right">Unverified Items</TableCell>
                      <TableCell align="right">Verify</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {submittedStandards.map((standard) => {
                      return (
                        <TableRow key={standard.id}>
                          <TableCell>
                            <Typography variant="h6">
                              {standard.title}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="h6">
                              {
                                actionItems
                                  ?.find((item) => item.id === standard.id)
                                  ?.actionItems.filter(
                                    (item) => item.done && !item.verifiedAt,
                                  ).length
                              }
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              onClick={() =>
                                navigate(
                                  Paths.STANDARDS_VERIFICATION_ACTION_ITEMS +
                                    "/" +
                                    standard.id,
                                )
                              }
                            >
                              <ArrowRightIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleOpenModal}
                sx={{
                  backgroundColor: "#faaa0a",
                  color: "#000",
                  fontWeight: 600,
                  "&:hover": { backgroundColor: "#e09a09" },
                }}
              >
                Complete Verification
              </Button>
            </Box>

            <Dialog
              open={modalOpen}
              onClose={() => setModalOpen(false)}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle sx={{ fontWeight: 600 }}>
                Complete Verification
              </DialogTitle>
              <DialogContent>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    New verified score
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {currentScore !== null ? `${currentScore}%` : "—"}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Unverified items
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {totalUnverifiedItems}
                  </Typography>
                </Box>

                {totalUnverifiedItems > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: "#fff8e1",
                      border: "1px solid #ffe082",
                    }}
                  >
                    <WarningAmberIcon sx={{ color: "#f9a825", mt: 0.25 }} />
                    <Typography variant="body2">
                      There are still <strong>{totalUnverifiedItems} unverified item{totalUnverifiedItems !== 1 ? "s" : ""}</strong> remaining.
                      These will be included in the score calculation. Consider verifying all items before completing.
                    </Typography>
                  </Box>
                )}
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                  onClick={() => setModalOpen(false)}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleConfirm}
                  disabled={submitting}
                  sx={{
                    backgroundColor: "#faaa0a",
                    color: "#000",
                    fontWeight: 600,
                    "&:hover": { backgroundColor: "#e09a09" },
                  }}
                >
                  {submitting ? "Submitting..." : "Confirm"}
                </Button>
              </DialogActions>
            </Dialog>
          </div>
        ) : null}
      </div>
    </Box>
  );
}
